/**
 * Helpers compartilhados pelos blocos do tema ODC.
 *
 * Os widgets `image` (upload/seleção) e `object_browser` (mode link) do Volto
 * podem gravar o valor como string (URL), objeto ({'@id': ...}) ou array de
 * objetos. Estes helpers normalizam qualquer um desses formatos para uma URL
 * utilizável na view.
 */
import { flattenToAppURL, isInternalURL } from '@plone/volto/helpers/Url/Url';

type ObjectBrowserItem = { '@id'?: string; [key: string]: unknown };

export type ImageFieldValue =
  | string
  | ObjectBrowserItem
  | ObjectBrowserItem[]
  | null
  | undefined;

export type LinkFieldValue =
  | string
  | ObjectBrowserItem
  | ObjectBrowserItem[]
  | null
  | undefined;

/**
 * Resolve o valor de um campo de imagem para uma URL renderizável.
 * URLs internas do CMS ganham o sufixo de scale (`/@@images/image/<scale>`).
 */
export const resolveImageUrl = (
  value: ImageFieldValue,
  scale: string = 'huge',
): string | undefined => {
  if (!value) return undefined;
  if (Array.isArray(value)) return resolveImageUrl(value[0], scale);
  if (typeof value === 'object') {
    const id = value['@id'];
    return id ? resolveImageUrl(id, scale) : undefined;
  }
  if (isInternalURL(value)) {
    const base = flattenToAppURL(value);
    // Evita duplicar o sufixo se a URL já apontar para um scale.
    return base.includes('@@images') ? base : `${base}/@@images/image/${scale}`;
  }
  return value;
};

/**
 * Larguras nominais dos scales do Plone (control panel de imagens). Os
 * blocos gravam só a URL da imagem, sem `image_scales`, então o `srcset` é
 * montado por nome: `preview` (400), `teaser` (600), `large` (800),
 * `larger` (1000), `great` (1200), `huge` (1600). Para imagens em pé o
 * scale sai mais estreito que o nominal — o navegador escolhe um degrau
 * abaixo do ideal, o que é aceitável.
 */
export const PLONE_SCALE_WIDTHS: Record<string, number> = {
  preview: 400,
  teaser: 600,
  large: 800,
  larger: 1000,
  great: 1200,
  huge: 1600,
};

/**
 * Monta um `srcset` com os scales pedidos para uma imagem do CMS. Para URL
 * externa (sem scales) devolve `undefined` — o `<img>` fica só com `src`.
 */
export const resolveImageSrcSet = (
  value: ImageFieldValue,
  scales: string[],
): string | undefined => {
  if (!value) return undefined;
  if (Array.isArray(value)) return resolveImageSrcSet(value[0], scales);
  if (typeof value === 'object') {
    const id = value['@id'];
    return id ? resolveImageSrcSet(id, scales) : undefined;
  }
  if (!isInternalURL(value) || value.includes('@@images')) return undefined;
  const base = flattenToAppURL(value);
  return scales
    .filter((scale) => PLONE_SCALE_WIDTHS[scale])
    .map(
      (scale) =>
        `${base}/@@images/image/${scale} ${PLONE_SCALE_WIDTHS[scale]}w`,
    )
    .join(', ');
};

/**
 * Resolve o valor de um campo de link (object_browser mode link ou URL)
 * para um href navegável. URLs internas viram caminhos relativos do app.
 */
export const resolveLinkHref = (value: LinkFieldValue): string | undefined => {
  if (!value) return undefined;
  if (Array.isArray(value)) return resolveLinkHref(value[0]);
  if (typeof value === 'object') {
    const id = value['@id'];
    return id ? resolveLinkHref(id) : undefined;
  }
  return isInternalURL(value) ? flattenToAppURL(value) : value;
};

/**
 * True se o href resolvido aponta para fora do site (abre em nova aba).
 */
export const isExternalHref = (href: string | undefined): boolean =>
  !!href && /^https?:\/\//.test(href);
